import { fetchSearchPage, searchQueryUrl, type SearchPage } from './search'

export type PagerOutcome =
  | { status: 'ok'; merged: SearchPage & { page: number } }
  | { status: 'stale' }
  | { status: 'error'; error: unknown; isFirstPage: boolean }
  | { status: 'skipped' }

export type SearchPager = {
  load(query: string): Promise<PagerOutcome>
  loadMore(query: string): Promise<PagerOutcome>
  bump(): void
  nextPage(): boolean
  paging(): boolean
}

export function createSearchPager(searchUrl: string): SearchPager {
  const cache = new Map<string, SearchPage & { page: number }>()
  let page = 1
  let nextPageFlag = false
  let paging = false
  let inflight = 0

  const bump = () => {
    inflight++
  }

  async function load(query: string): Promise<PagerOutcome> {
    page = 1
    nextPageFlag = false

    const cached = cache.get(query)

    if (cached) {
      page = cached.page
      nextPageFlag = cached.nextPage

      return { status: 'ok', merged: cached }
    }

    const ticket = ++inflight

    const result = await fetchSearchPage(searchQueryUrl(searchUrl, query, page))

    if (ticket !== inflight) return { status: 'stale' }

    if (result.status === 'error') {
      return { status: 'error', error: result.error, isFirstPage: true }
    }

    nextPageFlag = result.page.nextPage

    const merged: SearchPage & { page: number } = { ...result.page, page }

    cache.set(query, merged)

    return { status: 'ok', merged }
  }

  async function loadMore(query: string): Promise<PagerOutcome> {
    if (!nextPageFlag || paging) return { status: 'skipped' }

    const ticket = ++inflight

    paging = true

    const result = await fetchSearchPage(searchQueryUrl(searchUrl, query, page + 1))

    if (ticket !== inflight) {
      paging = false

      return { status: 'stale' }
    }

    paging = false

    if (result.status === 'error') {
      return { status: 'error', error: result.error, isFirstPage: false }
    }

    page += 1
    nextPageFlag = result.page.nextPage

    const acc = cache.get(query)
    const merged: SearchPage & { page: number } = {
      items: [...(acc?.items ?? []), ...result.page.items],
      nextPage: nextPageFlag,
      page
    }

    cache.set(query, merged)

    return { status: 'ok', merged }
  }

  return {
    load,
    loadMore,
    bump,
    nextPage: () => nextPageFlag,
    paging: () => paging
  }
}
